import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, BookOpen, GraduationCap, Target, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function Learning() {
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/learning/content`);
      setContent(response.data.content);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching learning content:", error);
      toast.error("Failed to fetch content");
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'beginner':
        return <BookOpen className="w-5 h-5" />;
      case 'intermediate':
        return <Target className="w-5 h-5" />;
      case 'advanced':
        return <Trophy className="w-5 h-5" />;
      default:
        return <GraduationCap className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'beginner':
        return 'text-[#00FFA3] bg-[#00FFA3]/10 border-[#00FFA3]/20';
      case 'intermediate':
        return 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/20';
      case 'advanced':
        return 'text-[#FF3B30] bg-[#FF3B30]/10 border-[#FF3B30]/20';
      default:
        return 'text-[#00E0FF] bg-[#00E0FF]/10 border-[#00E0FF]/20';
    }
  };

  const filteredContent = activeCategory === "all" 
    ? content 
    : content.filter(item => item.category === activeCategory);

  return (
    <div className="min-h-screen bg-[#030712] relative">
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      {/* Navigation */}
      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button 
            data-testid="back-btn"
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <GraduationCap className="w-10 h-10 text-[#00E0FF]" />
            <h1 className="text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Unbounded' }}>
              LEARNING HUB
            </h1>
          </div>
          <p className="text-gray-400">Educational resources for traders at every level</p>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <Card className="glass-effect">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#00FFA3]/10 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#00FFA3]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white mono">
                  {content.filter(c => c.category === 'beginner').length}
                </div>
                <div className="text-sm text-gray-400">Beginner Courses</div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#F59E0B]/10 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-[#F59E0B]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white mono">
                  {content.filter(c => c.category === 'intermediate').length}
                </div>
                <div className="text-sm text-gray-400">Intermediate Courses</div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-effect">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#FF3B30]/10 rounded-lg flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[#FF3B30]" />
              </div>
              <div>
                <div className="text-2xl font-bold text-white mono">
                  {content.filter(c => c.category === 'advanced').length}
                </div>
                <div className="text-sm text-gray-400">Advanced Courses</div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
            <TabsList className="glass-effect w-full justify-start mb-6">
              <TabsTrigger 
                data-testid="all-tab"
                value="all" 
                className="data-[state=active]:bg-[#00E0FF]/20 data-[state=active]:text-[#00E0FF]"
              >
                All Levels
              </TabsTrigger>
              <TabsTrigger 
                data-testid="beginner-tab"
                value="beginner"
                className="data-[state=active]:bg-[#00FFA3]/20 data-[state=active]:text-[#00FFA3]"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Beginner
              </TabsTrigger>
              <TabsTrigger 
                data-testid="intermediate-tab"
                value="intermediate"
                className="data-[state=active]:bg-[#F59E0B]/20 data-[state=active]:text-[#F59E0B]"
              >
                <Target className="w-4 h-4 mr-2" />
                Intermediate
              </TabsTrigger>
              <TabsTrigger 
                data-testid="advanced-tab"
                value="advanced"
                className="data-[state=active]:bg-[#FF3B30]/20 data-[state=active]:text-[#FF3B30]"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Advanced
              </TabsTrigger>
            </TabsList>

            <div className="mt-0">
              {loading ? (
                <div className="text-center py-12 text-gray-500">Loading content...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredContent.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      data-testid={`learning-card-${item.id}`}
                    >
                      <Card className="glass-effect h-full hover:border-[#00E0FF]/30 transition-all duration-300 hover:-translate-y-1">
                        <CardHeader>
                          <div className="flex items-start justify-between mb-2">
                            <CardTitle className="text-white text-xl" style={{ fontFamily: 'Unbounded' }}>
                              {item.title}
                            </CardTitle>
                            <div className={`px-3 py-1 rounded-full border flex items-center gap-2 ${getCategoryColor(item.category)}`}>
                              {getCategoryIcon(item.category)}
                              <span className="text-xs font-semibold uppercase">{item.category}</span>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <p className="text-gray-400 leading-relaxed mb-4">{item.content}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-600 px-2 py-1 bg-white/5 rounded">
                              {item.topic.replace('_', ' ').toUpperCase()}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </Tabs>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-12"
        >
          <Card className="glass-effect">
            <CardContent className="p-8 text-center">
              <GraduationCap className="w-16 h-16 mx-auto mb-4 text-[#00E0FF]" />
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Unbounded' }}>
                READY TO APPLY YOUR KNOWLEDGE?
              </h2>
              <p className="text-gray-400 mb-6">Start analyzing markets with AI-powered predictions</p>
              <Button 
                data-testid="start-trading-btn"
                onClick={() => navigate('/dashboard')}
                className="bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold px-8"
              >
                Start Trading
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
